// features/members/membersSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type Member = {
  name: string;
  email: string;
  role: string;
  lastLogin: string;
  addedDate: string;
  status: 'Active' | 'Revoke' | 'Pending' | 'Expired' | 'Canceled';
};

// Add type for raw API data
export type APIMember = {
  id: string;
  tenantId: string;
  rowType: string;
  memberId: string;
  userId: string;
  inviteId: string | null;
  email: string;
  displayName: string;
  roleId: string;
  roleName: string;
  status: string;
  lastLoginAt: string | null;
  addedAt: string;
  updatedAt: string;
};

type MembersState = {
  list: Member[];
  loading: boolean;
  error: string | null;
  rawMembersData: APIMember[]; // Members without 'Deleted role'
  rawInvitationsData: APIMember[]; // All invitations including 'Deleted role'
};

const initialState: MembersState = {
  list: [],
  loading: false,
  error: null,
  rawMembersData: [],
  rawInvitationsData: [],
};

const membersSlice = createSlice({
  name: 'members',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setMembers: (state, action: PayloadAction<Member[]>) => {
      state.list = action.payload;
      state.loading = false;
      state.error = null;
    },
    setRawMembersData: (state, action: PayloadAction<APIMember[]>) => {
      state.rawMembersData = action.payload;
    },
    setRawInvitationsData: (state, action: PayloadAction<APIMember[]>) => {
      state.rawInvitationsData = action.payload;
    },
    updateInvitationStatus: (
      state,
      action: PayloadAction<{ index: number; newStatus: string }>
    ) => {
      const { index, newStatus } = action.payload;
      if (state.rawInvitationsData[index]) {
        state.rawInvitationsData[index].status = newStatus;
      }
    },
    addMember: (state, action: PayloadAction<Member>) => {
      state.list.unshift(action.payload);
    },
    removeMember: (state, action: PayloadAction<number>) => {
      state.list.splice(action.payload, 1);
    },
    resendInvite: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (state.list[index]) {
        state.list[index].status = 'Pending';
      }
    },
    updateMemberRole: (
      state,
      action: PayloadAction<{ index: number; newRole: string }>
    ) => {
      if (state.list[action.payload.index]) {
        state.list[action.payload.index].role = action.payload.newRole;
      }
    },
    updateMemberStatus: (
      state,
      action: PayloadAction<{ index: number; newStatus: 'Active' | 'Revoke' | 'Pending' | 'Expired' | 'Canceled' }>
    ) => {
      const { index, newStatus } = action.payload;
      if (state.list[index]) {
        state.list[index].status = newStatus;
      }
    },
  },
});

export const {
  setLoading,
  setError,
  setMembers,
  setRawMembersData,
  setRawInvitationsData,
  updateInvitationStatus,
  addMember,
  removeMember,
  resendInvite,
  updateMemberRole,
  updateMemberStatus
} = membersSlice.actions;
export default membersSlice.reducer;